import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import { Alert, Snackbar, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { MessageContext } from '../context/message';

export const MessageProvider = ({ children }: { children: React.ReactNode }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const resetMessage = () => {
    setErrorMessage(null);
    setSuccessMessage(null);
    setInfoMessage(null);
  };

  const message: string | null = errorMessage || successMessage || infoMessage;
  const contextValue = useMemo(
    () => ({
      setErrorMessage,
      setSuccessMessage,
      setInfoMessage,
    }),
    [setErrorMessage, setSuccessMessage, setInfoMessage]
  );

  useEffect(() => setOpen(!!message), [message]);

  const onClose = () => {
    setOpen(false);
    setTimeout(() => resetMessage(), 750);
  };

  return (
    <MessageContext.Provider value={contextValue}>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        sx={{ zIndex: 9999, maxWidth: '500px' }}
        open={open}
        autoHideDuration={6000}
        onClose={onClose}
      >
        <Alert
          variant="standard"
          onClose={onClose}
          sx={{ textAlign: 'center' }}
          iconMapping={{
            success: <CheckCircleIcon fontSize="inherit" sx={{ color: 'success.main' }} />,
            error: <ErrorIcon fontSize="inherit" sx={{ color: 'error.main' }} />,
          }}
          action={
            <CloseIcon
              fontSize="small"
              sx={{ marginTop: '6px', cursor: 'pointer', fontSize: '20px' }}
              onClick={onClose}
            />
          }
          severity={successMessage ? 'success' : infoMessage ? 'info' : 'error'}
          data-cy={`alert-${successMessage ? 'success' : 'error'}`}
        >
          <Typography variant="subtitle2" data-cy="snackbar-message-text">
            {message}
          </Typography>
        </Alert>
      </Snackbar>
      {children}
    </MessageContext.Provider>
  );
};
